import { useCallback, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { ROLE_LABEL, useCurrentUser, useLogout } from '../api/session';
import { APP_MARK, APP_NAME } from '../brand';
import { Avatar } from '../components/Avatar';
import { Icon, type IconName } from '../components/Icon';
import { EmployeeDrawer } from './EmployeeDrawer';
import { PageTitleContext } from './shellContext';

const COLLAPSED_KEY = 'gw.sidebarCollapsed';
const isDesktop = () =>
  typeof window.matchMedia !== 'function' || window.matchMedia('(min-width: 981px)').matches;

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function NavItem({ to, label, icon }: { to: string; label: string; icon: IconName }) {
  return (
    <NavLink to={to} className="nav-item">
      <Icon name={icon} size={17} />
      <span>{label}</span>
    </NavLink>
  );
}

export function AppShell() {
  const [title, setTitle] = useState('');
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const me = useCurrentUser();
  const logout = useLogout();

  // Выдвижное меню на телефоне закрывается после перехода.
  // biome-ignore lint/correctness/useExhaustiveDependencies: реагируем именно на смену адреса
  useEffect(() => setMobileOpen(false), [location.pathname]);

  const toggleMenu = useCallback(() => {
    if (isDesktop()) {
      setCollapsed((value) => {
        try {
          window.localStorage.setItem(COLLAPSED_KEY, value ? '0' : '1');
        } catch {
          // хранилище недоступно — выбор живёт до перезагрузки
        }
        return !value;
      });
    } else {
      setMobileOpen((open) => !open);
    }
  }, []);

  const user = me.data;
  const roles = user?.roles ?? [];
  const isAdmin = roles.some((grant) => grant.role === 'admin' || grant.role === 'auditor');
  const menuShown = isDesktop() ? !collapsed : mobileOpen;

  return (
    <PageTitleContext.Provider value={setTitle}>
      <div className="shell" data-collapsed={collapsed} data-mobile-open={mobileOpen}>
        <aside id="app-navigation" className="sidebar" aria-label="Меню">
          <div className="brand">
            <Link to="/" className="brand-link">
              <span className="brand-mark" aria-hidden="true">
                {APP_MARK}
              </span>
              <span className="brand-text">
                <span className="brand-name">{APP_NAME}</span>
              </span>
            </Link>
            <button
              type="button"
              className="btn-icon ghost sidebar-close"
              aria-label="Скрыть меню"
              onClick={toggleMenu}
            >
              <Icon name="panelLeft" size={17} />
            </button>
          </div>

          <nav className="nav-group" aria-label="Разделы">
            {user?.employeeId ? <NavItem to="/me" label="Мой кабинет" icon="user" /> : null}
            <NavItem to="/org" label="Оргструктура" icon="network" />
            <NavItem to="/metrics" label="Показатели" icon="dashboard" />
            {me.data?.hasTeam || roles.some((grant) => grant.role === 'hr' || grant.role === 'admin') ? (
              <NavItem to="/attendance" label="Посещения" icon="clock" />
            ) : null}
            {me.data?.hasTeam || roles.some((grant) => grant.role === 'hr' || grant.role === 'admin') ? (
              <NavItem to="/payroll" label="Оклад и KPI" icon="wallet" />
            ) : null}
            {me.data?.hasTeam || roles.some((grant) => grant.role === 'hr' || grant.role === 'admin') ? (
              <NavItem to="/exports" label="Выгрузки" icon="download" />
            ) : null}
            {roles.some((grant) => grant.role === 'hr') ? (
              <>
                <NavItem to="/regulations" label="Регламенты" icon="book" />
                <NavItem to="/imports" label="Загрузка нарушений" icon="upload" />
              </>
            ) : null}
          </nav>

          {isAdmin ? (
            <nav className="nav-group" aria-label="Администрирование">
              <div className="nav-label">Администрирование</div>
              <NavItem to="/admin/access" label="Роли и соруководители" icon="key" />
              <NavItem to="/admin/security" label="Журнал безопасности" icon="shield" />
              <NavItem to="/admin/system" label="Состояние системы" icon="activity" />
            </nav>
          ) : null}

          {user ? (
            <div className="sidebar-footer">
              <div className="profile">
                <Avatar name={user.displayName} />
                <div className="profile-text">
                  <div className="profile-name">{user.displayName}</div>
                  {roles.length ? (
                    <div className="profile-role">
                      {[...new Set(roles.map((grant) => ROLE_LABEL[grant.role]))].join(', ')}
                    </div>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="btn-icon ghost"
                  aria-label="Выйти"
                  title="Выйти"
                  disabled={logout.isPending}
                  onClick={() =>
                    logout.mutate(undefined, { onSettled: () => navigate('/login', { replace: true }) })
                  }
                >
                  <Icon name="logout" />
                </button>
              </div>
            </div>
          ) : null}
        </aside>
        {mobileOpen ? (
          <button
            type="button"
            className="scrim"
            aria-label="Закрыть меню"
            onClick={() => setMobileOpen(false)}
          />
        ) : null}

        <main className="main">
          <header className="topbar">
            <button
              type="button"
              className="btn-icon"
              aria-label="Меню"
              aria-controls="app-navigation"
              aria-expanded={menuShown}
              onClick={toggleMenu}
            >
              <Icon name="panelLeft" size={18} />
            </button>
            <div className="topbar-title">
              <h1>{title}</h1>
            </div>
          </header>
          <Outlet />
        </main>
        <EmployeeDrawer />
      </div>
    </PageTitleContext.Provider>
  );
}
